import React, { useEffect } from "react"
import { Page } from "@shopify/polaris"
import { useHistory, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAppContext } from "components/AppContext"
import { PILLAR_ENVIRONMENTAL } from "utils/constants/pillars"
import AnalyzeTemplate from "views/templates/AnalyzeTemplate"
import ObjectiveNavigationTemplate from "views/templates/ObjectiveNavigationTemplate"
import usePillars from "views/organisms/pillars/usePillars"
import Loading from "components/widgets/Loading"
import withAuthRequired from "components/auth/withAuthRequired"
import AccessModal from "components/AccessModal"
import WebsiteExploreChartOrganism from "views/organisms/canvases/charts/analytics/WebsiteExploreChartOrganism"

export const explore_websites_URL = "/explore/websites"

const RGAA_ALIAS = "dashboard_websites_explore_rgaa"
const RGESN_ALIAS = "dashboard_websites_explore_rgesn"

// Two tabs per eligible page: "<page> - RGAA" and "<page> - RGESN".
const buildTabs = (auditWebsites, id) =>
  auditWebsites.flatMap((website) => [
    {
      id: `rgaa-${website.id}`,
      name: `${website.title} - RGAA`,
      url: `/explore/websites/rgaa-${website.id}`,
      selected: id === `rgaa-${website.id}`,
    },
    {
      id: `rgesn-${website.id}`,
      name: `${website.title} - RGESN`,
      url: `/explore/websites/rgesn-${website.id}`,
      selected: id === `rgesn-${website.id}`,
    },
  ])

const ExploreWebsitesPage = () => {
  const { id } = useParams()
  const history = useHistory()
  const { t } = useTranslation()

  const {
    currentPeriod,
    parentOrganization,
    envScore,
    isEnvScoreLoading,
    isCurrentNodeLoading,
    subscription,
    currentOrganization,
    currentUser,
  } = useAppContext()

  const auditWebsites = envScore?.website_analyse?.audit_websites || []
  const tabs = buildTabs(auditWebsites, id)

  useEffect(() => {
    if (isEnvScoreLoading || !envScore) return

    // No detailed (v2) audit available: nothing to show under this menu.
    if (auditWebsites.length === 0) {
      history.push("/explore")
      return
    }

    if (!id || !tabs.some((tab) => tab.id === id)) {
      history.push(tabs[0].url)
    }
  }, [envScore, isEnvScoreLoading, id])

  const objectives = [
    {
      objectiveId: 13,
      selected: true,
      url: tabs[0]?.url || explore_websites_URL,
    },
  ]

  const pillars = usePillars({
    pageName: explore_websites_URL,
  }).map((pillar) => ({
    ...pillar,
    selected: pillar.pillar === PILLAR_ENVIRONMENTAL,
  }))

  if (isEnvScoreLoading || isCurrentNodeLoading) {
    return <Loading />
  }

  const activeTab = tabs.find((tab) => tab.id === id) || tabs[0]
  const [type, websiteIdStr] = (activeTab?.id || "").split("-")
  const alias = type === "rgaa" ? RGAA_ALIAS : RGESN_ALIAS
  const websiteId = Number(websiteIdStr)

  const hasFeature = subscription?.features?.website_audit_details

  const paywallTexts = {
    description: t(
      "common:subscription_feature_missing.website_audit_details.description"
    ),
    mailSubject: t(
      "common:subscription_feature_missing.website_audit_details.mailTemplateSubject",
      { currentOrganizationName: currentOrganization?.name || "" }
    ),
    mailBody: t(
      "common:subscription_feature_missing.website_audit_details.mailTemplateBody",
      {
        currentUserFirstname: currentUser?.firstname || "",
        currentUserLastname: currentUser?.lastname || "",
      }
    ),
  }

  return (
    <ObjectiveNavigationTemplate>
      <Page fullWidth={!!envScore}>
        <AnalyzeTemplate
          parentOrganization={parentOrganization}
          themeType={PILLAR_ENVIRONMENTAL}
          objectives={objectives}
          pillars={pillars}
          tabs={hasFeature ? tabs : []}
          periodName={currentPeriod?.name}
        >
          {hasFeature && activeTab ? (
            <WebsiteExploreChartOrganism alias={alias} websiteId={websiteId} />
          ) : (
            <AccessModal open scoped texts={paywallTexts} />
          )}
        </AnalyzeTemplate>
      </Page>
    </ObjectiveNavigationTemplate>
  )
}

export default withAuthRequired(ExploreWebsitesPage)
